import { inject, Injectable } from '@angular/core';
import { WarehouseStateSnapshotPayload } from '../snapshot/warehouse-state-snapshot.dto';
import { SnapshotSessionKey, SnapshotSessionStore } from './snapshot-session.store';

/**
 * Whether the app is currently showing a historical warehouse state, and which one.
 *
 * Deliberately dependency-light — it injects only the payload store — because the
 * history-aware service wrappers read it, and those wrappers are themselves what
 * CurrentWarehouseDataSource injects. Anything heavier here reintroduces a DI cycle.
 *
 * WarehouseSourceService is the only writer.
 */
@Injectable()
export class HistoricalViewState {
  private readonly store = inject(SnapshotSessionStore, { optional: true });

  private active: SnapshotSessionKey | null = null;

  /**
   * Bumped on every mode change. Services that cache reads (SystemDrawingService caches by
   * division in a signal) compare against this to know their cache is from the other source.
   */
  private changeCount = 0;

  get generation(): number {
    return this.changeCount;
  }

  activate(key: SnapshotSessionKey): void {
    if (this.active?.divisionId === key.divisionId && this.active.snapshotId === key.snapshotId) {
      return;
    }

    this.active = key;
    this.changeCount++;
  }

  deactivate(): void {
    if (this.active) {
      this.active = null;
      this.changeCount++;
    }
  }

  isActive(divisionId?: string): boolean {
    return !!this.active && (!divisionId || this.active.divisionId === divisionId);
  }

  /** The payload to read instead of Firestore, or null to fall through to live data. */
  payloadFor(divisionId: string): WarehouseStateSnapshotPayload | null {
    const active = this.active;
    return active?.divisionId === divisionId ? (this.store?.payloadIfLoaded(active) ?? null) : null;
  }

  /** payloadFor(), but waits for an in-flight payload. Null (live) if none selected or load failed. */
  async payloadReady(divisionId: string): Promise<WarehouseStateSnapshotPayload | null> {
    const active = this.active;
    if (!this.store || active?.divisionId !== divisionId) {
      return null;
    }

    const payload = await this.store.whenLoaded(active);

    // Selection changed while waiting; answer for the new one.
    return this.active === active ? payload : this.payloadReady(divisionId);
  }
}
